import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Wallet, Gift, Sparkle, Loader2, MessageCircle, Check, Crown, ArrowRight, Send, Info } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import PayoutManager from "@/components/PayoutManager";

const PACKS = [
  { id: "pack10", label: "10-Card Pack", price: 79, credits: 10 },
  { id: "pack25", label: "25-Card Pack", price: 189, credits: 25 },
  { id: "pack50", label: "50-Card Pack", price: 349, credits: 50 },
];
const BONUS_PCT = 0.20;

/**
 * The Maison Wallet — internal USD credit ledger. Recipients claim funded cash gifts into
 * this balance (must be logged in — the ROI: every gift recipient becomes an account).
 * Spending the balance on a Credit Pack grants a +20% Reciprocity/Referral Bonus, keeping
 * capital circulating inside the estate instead of ever leaving the platform.
 *
 * "Add Balance" now lives ONLY in the Global App Header next to the gold balance ticker
 * (see AppShell.js) — not duplicated here, per the UI Polish directive.
 *
 * hasSentCards: passed by CommandCenter (already loads /cards) — when false, shows a
 * "Send your first card" Maison Preview teaser: they already have an account, so the
 * friction to their first purchase is now zero.
 */
const MaisonWallet = ({ hasSentCards = true }) => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [replies, setReplies] = useState({ replies: [], unread: 0 });
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState("");
  const [redeemMsg, setRedeemMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [w, r] = await Promise.all([api.get("/wallet"), api.get("/replies")]);
      setWallet(w.data);
      setReplies(r.data);
    } catch (e) { console.error("Maison Wallet load failed:", e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Maison Funding Station — confirm a top-up Stripe Checkout redirect (success/cancel)
  // coming back from stripe_service.create_wallet_topup_checkout's success_url
  // (/wallet?success=true&session_id=...) or cancel_url (/wallet?success=false). This effect
  // runs regardless of which route rendered this component (/dashboard or /wallet both mount
  // CommandCenter), so the balance + Transaction History update the moment the user lands back.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get("success");
    const sessionId = params.get("session_id");
    if (success === "true" && sessionId) {
      (async () => {
        try { await api.get(`/wallet/topup/verify/${sessionId}`); await load(); } catch (e) { console.error("Wallet top-up verify failed:", e); }
        window.history.replaceState({}, "", "/dashboard");
      })();
    } else if (success === "false") {
      window.history.replaceState({}, "", "/dashboard");
    }
  }, []);

  const redeem = async (packId) => {
    setRedeeming(packId); setRedeemMsg("");
    try {
      const res = await api.post("/wallet/redeem", { pack: packId });
      setRedeemMsg(`+${res.data.credits_added} credits added (${res.data.bonus_credits} bonus)! Premium unlocked.`);
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setRedeemMsg((d && typeof d === "object" ? d.message : d) || "Could not redeem right now.");
    } finally { setRedeeming(""); }
  };

  if (loading) return null;
  const balance = wallet?.balance || 0;
  const transactions = wallet?.transaction_ledger || [];

  return (
    <section id="wallet" className="mb-14" data-testid="maison-wallet">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif text-2xl flex items-center gap-2">
          <Wallet className="w-5 h-5 text-primary" /> The Maison Wallet
          {/* Utility Clarification — Store Readiness: Maison Credits are a redeemable
              services/goods ledger, never a digital-only in-app currency (Apple/Google
              digital-currency guideline compliance). */}
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" data-testid="maison-credits-info" aria-label="What are Maison Credits?"
                  className="inline-flex items-center justify-center w-5 h-5 rounded-full text-muted-foreground hover:text-primary hover:bg-muted/50 transition-colors">
                  <Info className="w-3.5 h-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent data-testid="maison-credits-tooltip" className="max-w-xs text-center">
                Maison Credits are redeemed for physical design deliverables and studio services.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </h2>
      </div>

      {/* Maison Preview — zero-friction nudge to their first send, since they already have an account */}
      {balance > 0 && !hasSentCards && (
        <button data-testid="wallet-first-card-teaser" onClick={() => navigate("/cards")}
          className="w-full flex items-center justify-between surface rounded-2xl p-4 mb-4 border border-primary/30 hover:bg-primary/5 text-left">
          <span className="flex items-center gap-2 text-sm"><Send className="w-4 h-4 text-primary" /> You have ${balance.toFixed(2)} waiting — send your first Maison card now, zero extra cost.</span>
          <ArrowRight className="w-4 h-4 text-primary shrink-0" />
        </button>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="surface rounded-[1.75rem] p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Balance</div>
          <div className="font-serif text-4xl gold-text mb-3" data-testid="wallet-balance">${balance.toFixed(2)}</div>
          <p className="text-sm text-muted-foreground mb-4">
            Cash gifts you&apos;ve claimed land here. Spend it on a Credit Pack for a <span className="gold-text font-medium">+20% Reciprocity Bonus</span> — keep your gifts circulating in the Maison.
          </p>
          {redeemMsg && <div className="text-sm text-secondary flex items-center gap-1.5 mb-2" data-testid="wallet-redeem-msg"><Check className="w-4 h-4" /> {redeemMsg}</div>}
          <div className="space-y-2">
            {PACKS.map((p) => {
              const canAfford = balance >= p.price;
              const bonus = Math.round(p.credits * BONUS_PCT);
              return (
                <button key={p.id} data-testid={`wallet-redeem-${p.id}`} onClick={() => redeem(p.id)} disabled={!canAfford || !!redeeming}
                  className={`w-full flex items-center justify-between text-sm rounded-xl px-4 py-2.5 border ${canAfford ? "border-primary/30 hover:bg-primary/10 text-foreground" : "border-border text-muted-foreground opacity-50"}`}>
                  <span className="flex items-center gap-1.5"><Gift className="w-3.5 h-3.5" /> {p.label} — ${p.price}</span>
                  <span className="flex items-center gap-1 gold-text">
                    {redeeming === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkle className="w-3 h-3" /> {p.credits + bonus} credits (+{bonus} bonus)</>}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Cash Out — the real bank/debit-card payout path. Deliberately given the SAME
            visual weight as the on-platform Credit Pack spend option above: recipients are
            never nudged harder toward spending than toward taking their money out. */}
        <PayoutManager balance={balance} onCashedOut={load} />

        <div className="surface rounded-[1.75rem] p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
            <MessageCircle className="w-3.5 h-3.5" /> Maison Replies {replies.unread > 0 && <span className="bg-primary/15 text-primary rounded-full px-2 py-0.5 text-[10px]">{replies.unread} new</span>}
          </div>
          {replies.replies.length === 0 ? (
            <p className="text-sm text-muted-foreground">Replies from your recipients will appear here.</p>
          ) : (
            <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="wallet-replies-list">
              {replies.replies.slice(0, 8).map((r) => (
                <div key={r.id} className="bg-muted/30 rounded-xl p-3" data-testid="wallet-reply-item">
                  <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5 flex-wrap">
                    <span>{r.from_name} · {r.occasion}</span>
                    {r.new_resident && (
                      <span data-testid="new-resident-badge" className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full"
                        style={{ background: "linear-gradient(135deg, #f6e3a3 0%, #c9a24a 50%, #8a6a2a 100%)", color: "#3d2e0f" }}>
                        <Crown className="w-2.5 h-2.5" /> New Resident
                      </span>
                    )}
                  </div>
                  {r.message && <p className="text-sm italic">&ldquo;{r.message}&rdquo;</p>}
                  {r.kind === "voice" && r.audio_base64 && (
                    <audio controls className="w-full mt-1.5" data-testid="wallet-reply-audio" src={`data:${r.audio_mime || "audio/webm"};base64,${r.audio_base64}`} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Maison Transaction History — every cash gift you've SENT, with a Recipient Pulse
          showing whether it's been claimed yet. */}
      <div className="surface rounded-[1.75rem] p-6 mt-6" data-testid="maison-transaction-history">
        <h3 className="font-serif text-xl mb-4">Maison Transaction History</h3>
        {transactions.length === 0 ? (
          <p className="text-sm text-muted-foreground">Cash gifts you send will show up here, along with whether your recipient has claimed them.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="transaction-history-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border/50">
                  <th className="py-2 pr-4">Recipient</th>
                  <th className="py-2 pr-4">Occasion</th>
                  <th className="py-2 pr-4">Amount</th>
                  <th className="py-2 pr-4">Maison Fee</th>
                  <th className="py-2">Recipient Pulse</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t.card_id} className="border-b border-border/30" data-testid="transaction-history-row">
                    <td className="py-2.5 pr-4">{t.recipient}</td>
                    <td className="py-2.5 pr-4 text-muted-foreground">{t.occasion}</td>
                    <td className="py-2.5 pr-4 gold-text font-medium">${Number(t.amount || 0).toFixed(2)}</td>
                    <td className="py-2.5 pr-4 text-muted-foreground">${Number(t.fee || 0).toFixed(2)}</td>
                    <td className="py-2.5">
                      {t.recipient_pulse === "cashed_out" ? (
                        <span data-testid="recipient-pulse-cashed-out" className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full font-medium bg-secondary/15 text-secondary border border-secondary/30">
                          Cashed Out
                        </span>
                      ) : t.recipient_pulse === "claimed" ? (
                        <span data-testid="recipient-pulse-claimed" className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full font-medium"
                          style={{ background: "linear-gradient(135deg, #f6e3a3 0%, #c9a24a 50%, #8a6a2a 100%)", color: "#3d2e0f" }}>
                          Claimed
                        </span>
                      ) : (
                        <span data-testid="recipient-pulse-awaiting" className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-muted/60 text-muted-foreground border border-border">
                          Awaiting Claim
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default MaisonWallet;
